import React, { useEffect, useRef } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";

import { faCircleCheck, faCircleXmark, faPencil } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Icon, Text } from "@performyard/py-components";

import Avatar from "../../../../../components/Avatar";
import FormattedDateTime from "../../../../../components/FormattedDateTime";
import InputCheckbox from "../../../../../components/Inputs/Checkbox";
import Tooltip from "../../../../../components/Tooltip";

import { useMeetingContext } from "../Meetings/MeetingContext";

export type ItemStatus = "complete" | "incomplete" | "deleted";
export type ItemType = "agenda" | "action";

export interface ChecklistItemProps {
	id: string;
	name: string;
	dueDate?: Date | null;
	status: ItemStatus;
	creatorId: string;
	userId: string;
	isEditing: boolean;
	assigneeId?: string;
	assigneeName?: string;
	isNewItem?: boolean;
	meetingId?: string;
	itemType: ItemType;
	onEdit: (id: string, name: string) => void;
	onNameChange: (value: string) => void;
	onStatusChange: (status: ItemStatus) => void;
	onSave: (save: boolean) => void;
	currentEditValue: string;
	onAssignmentClick?: (args: {
		itemId: string;
		meetingId: string;
		currentAssigneeId?: string;
		dueDate?: Date | null;
		name?: string;
	}) => void;
}

const ChecklistItem = ({
	id,
	name,
	dueDate,
	status,
	creatorId,
	userId,
	isEditing,
	assigneeId,
	assigneeName,
	isNewItem = false,
	meetingId,
	itemType,
	onEdit,
	onNameChange,
	onStatusChange,
	onSave,
	currentEditValue,
	onAssignmentClick,
}: ChecklistItemProps) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const { isInteractionDisabled } = useMeetingContext();

	const isActionItem = itemType === "action";
	const testIdPrefix = isActionItem ? "action-item" : "agenda-item";

	const isChecked = status === "complete";
	const isCreator = userId === creatorId;
	const isAssignee = !!assigneeId && userId === assigneeId;
	const isCheckboxDisabled =
		isInteractionDisabled || (!isActionItem && !isCreator) || (isActionItem && !isAssignee && !isCreator);

	// Focus textarea when entering edit mode
	useEffect(() => {
		if (isEditing && textareaRef.current) {
			textareaRef.current.select();
		}
	}, [isEditing]);

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			if (currentEditValue.trim()) {
				onSave(true);
			}
		} else if (e.key === "Escape") {
			onSave(false);
		}
	};

	const handleNameChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onNameChange(e.target.value);
	};

	const handleStatusChange = (isChecked: boolean) => {
		if (isInteractionDisabled) return;
		onStatusChange(isChecked ? "complete" : "incomplete");
	};

	const handleAssignmentClick = () => {
		if (isInteractionDisabled) return;

		if (!isNewItem && meetingId && onAssignmentClick) {
			onAssignmentClick({
				itemId: id,
				meetingId,
				currentAssigneeId: assigneeId,
				dueDate,
				name,
			});
		}
	};

	const renderContent = () => {
		if (isEditing) {
			return (
				<div className="flex items-center justify-between rounded-md p-2 outline outline-1 outline-neutral-3">
					<div className="flex w-full items-center space-x-2">
						<InputCheckbox value={isChecked} disabled={true} />
						<div className="flex w-full flex-col space-y-[7px]">
							<ReactTextareaAutosize
								ref={textareaRef}
								id={`${id}-input`}
								data-testid={`${testIdPrefix}-${id}-input`}
								className="w-full resize-none border-0 bg-transparent p-0 text-paragraph-3"
								value={currentEditValue}
								onChange={handleNameChange}
								placeholder={`Your ${itemType} item goes here`}
								minRows={1}
								onKeyDown={handleKeyDown}
								autoFocus
							/>
							{dueDate && (
								<Text size="extra-small" weight="regular">
									Due Date:{" "}
									{
										<FormattedDateTime
											date={dueDate}
											delimiter={"•" as const}
											hasTime={true}
											dateSize="medium"
											size="extra-small"
											weight="regular"
										/>
									}
								</Text>
							)}
						</div>
					</div>
					<div className="flex items-center">
						<button
							data-testid={`${testIdPrefix}-${id}-confirm-button`}
							className="flex h-6 w-6 items-center justify-center border-0 bg-transparent"
							disabled={!currentEditValue.trim()}
							onClick={() => onSave(true)}
						>
							<Icon
								icon={faCircleCheck}
								size={16}
								color="green-3"
								disabled={!currentEditValue.trim()}
								dangerouslySetClassName={`${
									currentEditValue.trim()
										? "hover:cursor-pointer hover:text-green-5 active:text-green-7"
										: "cursor-not-allowed"
								}`}
							/>
						</button>
						<button
							data-testid={`${testIdPrefix}-${id}-cancel-button`}
							className="flex h-6 w-6 items-center justify-center border-0 bg-transparent"
							onClick={() => onSave(false)}
						>
							<Icon
								icon={faCircleXmark}
								size={16}
								color="red-3"
								dangerouslySetClassName={"hover:cursor-pointer hover:text-red-5 active:text-red-7"}
							/>
						</button>
					</div>
				</div>
			);
		}

		return (
			<div className="flex items-center justify-between rounded-md bg-neutral-1 p-2 outline outline-1 outline-neutral-3">
				<div className="flex items-center space-x-2">
					<InputCheckbox
						value={isChecked}
						onChange={handleStatusChange}
						disabled={isCheckboxDisabled}
						data-testid={`${testIdPrefix}-${id}-checkbox`}
					/>
					<div className="flex w-full flex-col space-y-2">
						<Text size="paragraph-3" weight="regular">
							{name}
						</Text>
						{dueDate && (
							<Text size="extra-small" weight="regular">
								Due Date:{" "}
								{
									<FormattedDateTime
										date={dueDate}
										delimiter={"•" as const}
										hasTime={true}
										dateSize="medium"
										size="extra-small"
										weight="regular"
									/>
								}
							</Text>
						)}
					</div>
				</div>
				<div className="flex items-center">
					<button
						data-testid={`${testIdPrefix}-${id}-edit-button`}
						className="flex h-6 w-6 items-center justify-center border-0 bg-transparent"
						onClick={isCreator && !isInteractionDisabled ? () => onEdit(id, name) : undefined}
					>
						<Icon
							icon={faPencil}
							size={16}
							color="neutral-3"
							disabled={!isCreator || isInteractionDisabled}
							dangerouslySetClassName={`${
								isCreator && !isInteractionDisabled
									? "hover:cursor-pointer hover:text-neutral-5 active:text-neutral-7"
									: "cursor-not-allowed"
							}`}
						/>
					</button>
					<button
						data-testid={`${testIdPrefix}-${id}-delete-button`}
						className="flex h-6 w-6 items-center justify-center border-0 bg-transparent"
						onClick={isCreator && !isInteractionDisabled ? () => onStatusChange("deleted") : undefined}
					>
						<Icon
							icon={faCircleXmark}
							size={16}
							color="neutral-3"
							disabled={!isCreator || isInteractionDisabled}
							dangerouslySetClassName={`${
								isCreator && !isInteractionDisabled
									? "hover:cursor-pointer hover:text-neutral-5 active:text-neutral-7"
									: "cursor-not-allowed"
							}`}
						/>
					</button>
				</div>
			</div>
		);
	};

	return (
		<div className="flex items-center gap-2">
			<div className="flex-1">{renderContent()}</div>
			{isActionItem && (
				<div
					className={`${!isNewItem && isCreator && !isInteractionDisabled ? "hover:cursor-pointer" : "cursor-not-allowed opacity-50"}`}
				>
					<Tooltip message={!assigneeId ? "Unassigned" : assigneeName || ""} placement="top">
						<div onClick={isCreator && !isInteractionDisabled ? handleAssignmentClick : undefined}>
							<Avatar id={assigneeId} size={24} testId={`${testIdPrefix}-${id}-avatar`} />
						</div>
					</Tooltip>
				</div>
			)}
		</div>
	);
};

export default ChecklistItem;
